import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  findByIso,
  searchCountries,
  type CountryDialCode,
} from "@/lib/countryDialCodes";

interface Props {
  /** Selected ISO code, e.g. "IN". */
  value: string;
  onChange: (iso: string) => void;
  className?: string;
}

/**
 * Country dialling-code picker.
 *
 * Shows "IN +91 India" while choosing but collapses to "IN +91" once selected —
 * the country name has done its job by then, and the compact form leaves room
 * for the number itself, which is what you actually check when proof-reading.
 */
export default function CountryCodePicker({ value, onChange, className }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = findByIso(value);
  const matches = useMemo(() => searchCountries(query), [query]);

  // Close when focus leaves the widget entirely. Pointerdown rather than click
  // so the menu closes on press, matching every other dropdown on the page.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  // Keep the highlighted row in view when arrowing past the visible window.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[highlight] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  const commit = (c: CountryDialCode) => {
    onChange(c.iso);
    setOpen(false);
    setQuery("");
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        setOpen(true);
        setHighlight(0);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (matches[highlight]) commit(matches[highlight]);
    } else if (e.key === "Escape") {
      // Stop here so the surrounding modal doesn't also close — dismissing the
      // whole form because you backed out of a dropdown would lose the draft.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div ref={wrapRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setQuery("");
          setHighlight(0);
        }}
        onKeyDown={onKeyDown}
        className="w-full flex items-center justify-between gap-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
      >
        <span className="font-mono text-xs whitespace-nowrap">
          {selected ? `${selected.iso} +${selected.dial}` : "Select"}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-10 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg">
          <input
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Search country or code"
            className="w-full px-2.5 py-2 text-xs border-b border-gray-200 rounded-t-md focus:outline-none"
          />
          <ul ref={listRef} className="max-h-52 overflow-y-auto py-1">
            {matches.length === 0 && (
              <li className="px-2.5 py-2 text-xs text-gray-500">No match</li>
            )}
            {matches.map((c, i) => (
              <li key={`${c.iso}-${c.dial}`}>
                <button
                  type="button"
                  onClick={() => commit(c)}
                  onMouseEnter={() => setHighlight(i)}
                  className={cn(
                    "w-full text-left px-2.5 py-1.5 text-xs flex items-center gap-2",
                    i === highlight ? "bg-emerald-50" : "hover:bg-gray-50",
                    c.iso === value && "font-semibold",
                  )}
                >
                  <span className="font-mono text-gray-900 w-6 shrink-0">{c.iso}</span>
                  <span className="font-mono text-gray-600 w-12 shrink-0">+{c.dial}</span>
                  <span className="text-gray-700 truncate">{c.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
